
import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { type ContactFormState, submitContactForm } from "@/app/actions";
import type { ContactCategory, ContactOverlayDefaults } from "@/components/contact-overlay-context";
import { ChevronDown, Loader2, Send } from "lucide-react";

type ContactFormProps = {
  defaults?: ContactOverlayDefaults;
  onSuccess?: () => void;
  labels?: any;
};

export function ContactForm({ defaults, onSuccess, labels = {} }: ContactFormProps) {
  const initialState: ContactFormState = { success: false, message: "" };
  const [state, setState] = useState<ContactFormState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);

  const initialCategory = (defaults?.category ?? "Allgemeines") as ContactCategory;
  const [category, setCategory] = useState<ContactCategory>(initialCategory);
  
  // Form State for Validation
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [privacy, setPrivacy] = useState(false);

  const isFormValid = firstName.trim().length > 0 && 
                      lastName.trim().length > 0 && 
                      email.trim().length > 0 && 
                      privacy;
  
  // Custom German Validation Messages
  const handleInvalid = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    if (target.validity.valueMissing) {
        target.setCustomValidity("Bitte füllen Sie dieses Feld aus.");
    } else if (target.validity.typeMismatch) {
        if (target.type === "email") {
            target.setCustomValidity("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
        }
    } else {
        target.setCustomValidity("");
    }
  };

  const handleInput = (e: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    (e.target as HTMLInputElement).setCustomValidity("");
  };

  useEffect(() => {
    if (!state.message) return;

    if (state.success) {
      toast({
        title: "Nachricht gesendet",
        description: state.message,
      });
      formRef.current?.reset();
      setCategory((defaults?.category ?? "Allgemeines") as ContactCategory);
      // Reset local state
      setFirstName("");
      setLastName("");
      setEmail("");
      setPrivacy(false);
      
      onSuccess?.();
      return;
    }

    toast({
      title: "Formular unvollständig",
      description: state.message,
      variant: "destructive",
    });
  }, [state, defaults, onSuccess]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const formData = new FormData(event.currentTarget);
      const nextState = await submitContactForm(formData);
      setState(nextState);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="contact-category" className="text-sm font-semibold text-foreground">
            Kategorie
          </label>
          <div className="relative">
            <select
              id="contact-category"
              name="category"
              value={category}
              onChange={(e) => {
                const next = e.target.value as ContactCategory;
                setCategory(next);
              }}
              onInvalid={handleInvalid}
              onInput={handleInput}
              className="flex h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              required
            >
              <option value="Allgemeines">Allgemeines</option>
              <option value="Probetraining">Probetraining</option>
              <option value="Mitgliedschaft">Mitgliedschaft</option>
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="contact-first-name" className="text-sm font-semibold text-foreground">
            Vorname
          </label>
          <Input
            id="contact-first-name"
            name="firstName"
            autoComplete="name"
            placeholder=""
            required
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="contact-last-name" className="text-sm font-semibold text-foreground">
            Nachname
          </label>
          <Input
            id="contact-last-name"
            name="lastName"
            autoComplete="family-name"
            placeholder=""
            required
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="contact-email" className="text-sm font-semibold text-foreground">
            E-Mail
          </label>
          <Input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder=""
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="contact-phone" className="text-sm font-semibold text-foreground">
            Handynummer
          </label>
          <Input
            id="contact-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            placeholder=""
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <label htmlFor="contact-message" className="text-sm font-semibold text-foreground">
            Nachricht
          </label>
          <Textarea
            id="contact-message"
            name="message"
            placeholder=""
            className="min-h-[80px]"
            required={category === "Allgemeines"}
            onInvalid={handleInvalid}
            onInput={handleInput}
          />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-background/40 px-3 py-3">
        <input
          id="contact-privacy"
          name="privacy"
          type="checkbox"
          value="yes"
          required
          checked={privacy}
          onChange={(e) => setPrivacy(e.target.checked)}
          onInvalid={(e) => {
            (e.target as HTMLInputElement).setCustomValidity("Bitte stimmen Sie den Datenschutzbestimmungen zu.");
          }}
          onInput={handleInput}
          className="mt-0.5 h-5 w-5 min-w-[1.25rem] rounded border border-white/20 bg-background/60 checked:bg-white checked:border-white accent-white cursor-pointer"
        />
        <label htmlFor="contact-privacy" className="text-sm text-foreground/80 leading-snug">
          {labels.label_privacy || "Ich stimme zu, dass meine Angaben zur Bearbeitung der Anfrage gespeichert und verarbeitet werden."}
        </label>
      </div>

      <SubmitButton disabled={!isFormValid} loading={isSubmitting} labels={labels} />
    </form>
  );
}

function SubmitButton({ disabled, loading, labels }: { disabled: boolean; loading: boolean; labels?: any }) {
  return (
    <Button 
        type="submit" 
        className="w-full transition-opacity duration-200" 
        disabled={loading || disabled}
    >
      {loading ? (
        <span className="inline-flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Senden...
        </span>
      ) : (
        <span className="inline-flex items-center justify-center gap-2">
          <Send className="h-4 w-4" />
          {labels?.button_submit || "Nachricht senden"}
        </span>
      )}
    </Button>
  );
}

